/**
 * The one signed-webhook format for everything Pagerless sends or receives (outbound channel
 * webhooks, watchdog canary acks, fake servers):
 *
 *   Pagerless-Signature: t=<unix seconds>,v1=<hex HMAC-SHA256(secret, `${t}.${rawBody}`)>
 *
 * Receivers reject timestamps outside the tolerance (default 300 s) and accept any of several
 * v1 values and secrets, so a secret can be rotated without dropping deliveries. Web Crypto only.
 */

export const WEBHOOK_SIGNATURE_HEADER = "Pagerless-Signature";
export const WEBHOOK_TOLERANCE_S = 300;

const enc = new TextEncoder();

async function hmacHex(secret: string, message: string): Promise<string> {
  const key = await crypto.subtle.importKey("raw", enc.encode(secret), { name: "HMAC", hash: "SHA-256" }, false, [
    "sign",
  ]);
  const sig = new Uint8Array(await crypto.subtle.sign("HMAC", key, enc.encode(message)));
  let hex = "";
  for (const b of sig) hex += b.toString(16).padStart(2, "0");
  return hex;
}

/** Constant-time comparison (length is not secret: it is always 64 hex chars). */
function safeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}

/** Header value for `body` signed at `nowMs`. */
export async function signWebhook(secret: string, body: string, nowMs: number): Promise<string> {
  const t = Math.floor(nowMs / 1000);
  return `t=${t},v1=${await hmacHex(secret, `${t}.${body}`)}`;
}

export type WebhookVerifyResult = { ok: true } | { ok: false; reason: "malformed" | "stale" | "mismatch" };

/** Verifies a Pagerless-Signature header against any of `secrets` (rotation). */
export async function verifyWebhook(
  secrets: readonly string[],
  header: string | null | undefined,
  body: string,
  nowMs: number,
  toleranceS = WEBHOOK_TOLERANCE_S,
): Promise<WebhookVerifyResult> {
  let t: number | null = null;
  const v1: string[] = [];
  for (const part of (header ?? "").split(",")) {
    const i = part.indexOf("=");
    if (i < 0) continue;
    const k = part.slice(0, i).trim();
    const v = part.slice(i + 1).trim();
    if (k === "t" && /^\d{1,12}$/.test(v)) t = Number(v);
    else if (k === "v1" && /^[0-9a-f]{64}$/.test(v)) v1.push(v);
  }
  if (t === null || v1.length === 0 || secrets.length === 0) return { ok: false, reason: "malformed" };
  if (Math.abs(Math.floor(nowMs / 1000) - t) > toleranceS) return { ok: false, reason: "stale" };
  for (const secret of secrets) {
    const expected = await hmacHex(secret, `${t}.${body}`);
    if (v1.some((s) => safeEqual(s, expected))) return { ok: true };
  }
  return { ok: false, reason: "mismatch" };
}
